// The action platform's REST API (sdk/CONTRACT.md, "REST API"), and the hooks
// the AI tab and a workspace's Actions page read it through.
//
// The same client as collections and the seen area (`call`): the session
// cookie on every request, the server's own sentence on every refusal. Runs
// stay live through the image's event stream; when the stream fails, the list
// is polled every 5 s while a run is on its way. AI outcomes never go to the
// notification bell: they live here, beside the image.
//
// Through a share link the AI tab reads the link's namespace (`shareBase`),
// which serves an image's actions and runs and nothing else: no settings, no
// run started or cancelled, no event stream (the list is polled instead).

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { getConfig } from '../../config/runtimeConfig';
import { isRefusal } from '../../lib/httpError';
import { call } from '../collections/api';
import { isActive } from './states';
import type { Action, Run, StartRunRequest, WorkspaceEntry, WorkspaceAction, WorkspaceActions } from './types';

const seg = encodeURIComponent;

/** How often the runs are asked for while the stream is down and a run is on its way. */
export const POLL_MS = 5000;

/**
 * Where the AI tab reads, under the API root: '' for the app's own routes, or
 * a share link's namespace (shareBase). Part of every key, so what a link
 * shows and what the workspace shows are never one cache entry.
 */
export type ActionsBase = string;

/** A share link's namespace: the image's actions and runs, read only. */
export const shareBase = (shareId: string): ActionsBase => `/s/${encodeURIComponent(shareId)}`;

export const actionKeys = {
  all: ['ai'] as const,
  /** Every image's actions: what a workspace's change makes stale. */
  imageActionsAll: ['ai', 'image-actions'] as const,
  imageActions: (imageId: string, base: ActionsBase = '') => ['ai', 'image-actions', imageId, base] as const,
  workspaceActions: (workspaceId: string) => ['ai', 'workspace-actions', workspaceId] as const,
  runs: (imageId: string, base: ActionsBase = '') => ['ai', 'runs', imageId, base] as const,
};

/** `{ actions }`; a bare array is read too. */
const actionsOf = <T>(body: unknown): T[] =>
  (Array.isArray(body) ? body : Array.isArray((body as { actions?: unknown } | undefined)?.actions) ? (body as { actions: unknown[] }).actions : []) as T[];

/** What the AI tab offers on an image: its workspace's actions, on or off, with the values a run starts from (none through a link). */
export const listImageActions = async (imageId: string, base: ActionsBase = ''): Promise<Action[]> =>
  actionsOf<Action>(await call<unknown>(`${base}/images/${seg(imageId)}/actions`));

/** A workspace's actions: those this deployment grants it, with its own use of each. */
export const getWorkspaceActions = (workspaceId: string): Promise<WorkspaceActions> => call(`/workspaces/${seg(workspaceId)}/actions`);

export interface WorkspaceChange {
  enabled?: boolean;
  /** Replaces the workspace's entries whole. */
  settings?: Record<string, WorkspaceEntry>;
}

export const saveWorkspaceAction = (workspaceId: string, actionId: string, change: WorkspaceChange): Promise<WorkspaceAction> =>
  call(`/workspaces/${seg(workspaceId)}/actions/${seg(actionId)}`, { method: 'PUT', body: JSON.stringify(change) });

/** Newest first, cancelled left out (`{ runs }`; a bare array is read too). */
export const listRuns = async (imageId: string, base: ActionsBase = ''): Promise<Run[]> => {
  const body = await call<unknown>(`${base}/images/${seg(imageId)}/runs`);
  const runs = (Array.isArray(body) ? body : ((body as { runs?: unknown } | undefined)?.runs ?? [])) as Run[];
  return runs.reduce<Run[]>((list, r) => mergeRun(list, r), []);
};

export const getRun = (runId: string): Promise<Run> => call(`/runs/${seg(runId)}`);

export const startRun = (imageId: string, req: StartRunRequest): Promise<Run> =>
  call(`/images/${seg(imageId)}/runs`, { method: 'POST', body: JSON.stringify(req) });

export const cancelRun = (runId: string): Promise<Run | undefined> =>
  call(`/runs/${seg(runId)}/cancel`, { method: 'POST' });

export const runEventsUrl = (imageId: string): string => `${getConfig().apiRoot}/images/${seg(imageId)}/runs/events`;

const newestFirst = (a: Run, b: Run) => (b.createdUtc ?? '').localeCompare(a.createdUtc ?? '');

/**
 * The image's runs with one run's latest news in it: replaced where it is,
 * added where it is not, taken out once cancelled (a cancelled run is not
 * listed). Newest first.
 */
export function mergeRun(list: Run[], run: Run): Run[] {
  const rest = list.filter((r) => r.id !== run.id);
  if (run.status === 'cancelled' || run.state === 'cancelled') return rest;
  return [...rest, run].sort(newestFirst);
}

/** The image's actions, as its workspace has them. */
export function useImageActions(imageId: string | undefined, base: ActionsBase = '') {
  return useQuery({
    queryKey: actionKeys.imageActions(imageId ?? '', base),
    queryFn: () => listImageActions(imageId!, base),
    enabled: !!imageId,
    staleTime: 60_000,
  });
}

/** A workspace's Actions page. */
export function useWorkspaceActions(workspaceId: string | undefined) {
  return useQuery({ queryKey: actionKeys.workspaceActions(workspaceId ?? ''), queryFn: () => getWorkspaceActions(workspaceId!), enabled: !!workspaceId });
}

/**
 * The image's runs. `poll` asks again every 5 s while one of them is on its
 * way — the fallback for a failed event stream — and stops once the server
 * refuses (a link reset or made Private, an image no longer yours): the run
 * still "on its way" in the cache would otherwise keep it asking for good.
 */
export function useImageRuns(imageId: string | undefined, { poll = false, base = '' }: { poll?: boolean; base?: ActionsBase } = {}) {
  return useQuery({
    queryKey: actionKeys.runs(imageId ?? '', base),
    queryFn: () => listRuns(imageId!, base),
    enabled: !!imageId,
    staleTime: 10_000,
    refetchInterval: (q) => (poll && !isRefusal(q.state.error) && (q.state.data ?? []).some(isActive) ? POLL_MS : false),
  });
}

function putRun(qc: QueryClient, imageId: string, run: Run): void {
  qc.setQueryData<Run[]>(actionKeys.runs(imageId), (old) => mergeRun(old ?? [], run));
}

export function useStartRun(imageId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: StartRunRequest) => startRun(imageId!, req),
    // An identical run already on its way is what comes back; either way it is
    // the one to show.
    onSuccess: (run) => {
      if (imageId && run?.id) putRun(qc, imageId, run);
    },
  });
}

export function useCancelRun(imageId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (runId: string) => cancelRun(runId),
    onSuccess: (run, runId) => {
      if (!imageId) return;
      qc.setQueryData<Run[]>(actionKeys.runs(imageId), (old) => (old ?? []).filter((r) => r.id !== runId));
      if (run?.id) putRun(qc, imageId, run);
      void qc.invalidateQueries({ queryKey: actionKeys.runs(imageId) });
    },
  });
}

export interface RunEvents {
  /** The stream is up. */
  connected: boolean;
  /** The stream failed (or this browser has none): the runs are polled instead. */
  failed: boolean;
}

/**
 * The image's event stream: one `run` event per change to one of its runs,
 * each written straight into the runs cache. The session cookie authenticates
 * it (an EventSource cannot carry a header; withCredentials for a dev server
 * on another origin). The browser reconnects by itself after a drop; after a
 * reconnect the list is read again, for what changed while it was down. A
 * refused stream stays closed, and the caller polls.
 */
export function useRunEvents(imageId: string | undefined): RunEvents {
  const qc = useQueryClient();
  const [state, setState] = useState<RunEvents>({ connected: false, failed: false });
  useEffect(() => {
    setState({ connected: false, failed: false });
    if (!imageId) return;
    if (typeof EventSource === 'undefined') {
      setState({ connected: false, failed: true });
      return;
    }
    let es: EventSource;
    try {
      es = new EventSource(runEventsUrl(imageId), { withCredentials: true });
    } catch {
      setState({ connected: false, failed: true });
      return;
    }
    let opened = false;
    es.onopen = () => {
      if (opened) void qc.invalidateQueries({ queryKey: actionKeys.runs(imageId) });
      opened = true;
      setState({ connected: true, failed: false });
    };
    es.onerror = () => setState({ connected: false, failed: true });
    const onRun = (e: MessageEvent) => {
      let run: Run;
      try {
        run = JSON.parse(String(e.data)) as Run;
      } catch {
        return;
      }
      if (!run?.id || (run.imageId && run.imageId !== imageId)) return;
      // Before the list has loaded, the list is the answer: it is on its way.
      if (qc.getQueryData(actionKeys.runs(imageId)) === undefined) return;
      putRun(qc, imageId, run);
    };
    es.addEventListener('run', onRun as EventListener);
    return () => {
      es.removeEventListener('run', onRun as EventListener);
      es.close();
    };
  }, [imageId, qc]);
  return state;
}

/**
 * The image's runs, kept live: the stream, and polling when it fails. A share
 * link has no stream (its namespace serves none), so there the list is polled
 * while a run is on its way.
 */
export function useLiveImageRuns(imageId: string | undefined, base: ActionsBase = '') {
  const events = useRunEvents(base ? undefined : imageId);
  return useImageRuns(imageId, { poll: !!base || events.failed, base });
}

export function useSaveWorkspaceAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ workspaceId, actionId, change }: { workspaceId: string; actionId: string; change: WorkspaceChange }) =>
      saveWorkspaceAction(workspaceId, actionId, change),
    // The answer is the action with the workspace's new use: on the page at
    // once; and every image's AI tab reads its actions again, since what a run
    // starts from has changed.
    onSuccess: (saved, { workspaceId }) => {
      if (saved?.id) {
        qc.setQueryData<WorkspaceActions>(actionKeys.workspaceActions(workspaceId), (old) =>
          old ? { ...old, actions: old.actions.map((m) => (m.id === saved.id ? saved : m)) } : old,
        );
      }
      void qc.invalidateQueries({ queryKey: actionKeys.workspaceActions(workspaceId) });
      void qc.invalidateQueries({ queryKey: actionKeys.imageActionsAll });
    },
  });
}
